import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, ShieldCheck, Flower2, BookOpen, Gem, Sparkles, Languages, Play, Check, TrendingUp, ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import { Piko } from '../components/Piko';
import { audio } from '../core/audio';
import { getState, setState, isDailyRewardAvailable, dailyRewardCount, claimDailyReward } from '../core/storage';
import { t } from '../core/i18n';
import { WORLDS, REGIONS, currentJourney, getGame, gameName, gameLockState, starsNeeded } from '../content/games';
import { DAILY_STICKERS } from '../content/collection';
import { useEntitlement } from '../core/entitlement';

function MindaModal({ st, stars, entitlement, en, onClose }) {
  const played = Object.values(st.gameStats || {}).filter((g) => g.completed || g.level > 1);
  const curLevel = played.length ? Math.max(...played.map((g) => g.level || 1)) : 1;
  const earned = st.progress.starsEarned ?? stars;
  let next = null;
  outer: for (const r of REGIONS) {
    for (const gid of r.games) {
      if (gameLockState(entitlement, gid, stars) === 'stars') { next = { game: getGame(gid), need: starsNeeded(gid) }; break outer; }
    }
  }
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-40 bg-navy/50 backdrop-blur-md flex items-center justify-center px-6" onClick={onClose} data-testid="minda-modal">
      <motion.div initial={{ scale: 0.9, y: 18 }} animate={{ scale: 1, y: 0 }} onClick={(e) => e.stopPropagation()}
        className="rounded-[2rem] p-6 w-full max-w-sm shadow-2xl" data-testid="minda-stars"
        style={{ background: 'linear-gradient(150deg, #1D3557, #2A4A76)', border: '4px solid rgba(255,255,255,0.9)' }}>
        <div className="flex items-center gap-2">
          <Sparkles size={20} className="text-sunshine" />
          <h2 className="font-heading text-lg font-bold text-white">{en ? 'Minda Stars' : 'Bintang Minda'}</h2>
        </div>
        <div className="grid grid-cols-3 gap-2 mt-3">
          <div className="rounded-2xl bg-white/10 px-2 py-3 text-center" data-testid="minda-total">
            <p className="font-heading text-2xl font-bold text-sunshine leading-none">{earned}</p>
            <p className="font-body text-[10px] font-bold text-white/70 mt-1.5">{en ? 'Collected' : 'Dikumpul'}</p>
          </div>
          <div className="rounded-2xl bg-white/10 px-2 py-3 text-center" data-testid="minda-available">
            <p className="font-heading text-2xl font-bold text-white leading-none">{stars}</p>
            <p className="font-body text-[10px] font-bold text-white/70 mt-1.5">{en ? 'Available' : 'Sedia Guna'}</p>
          </div>
          <div className="rounded-2xl bg-white/10 px-2 py-3 text-center" data-testid="minda-level">
            <p className="font-heading text-2xl font-bold text-leaf leading-none">{curLevel}</p>
            <p className="font-body text-[10px] font-bold text-white/70 mt-1.5">{en ? 'Level' : 'Tahap'}</p>
          </div>
        </div>
        <div className="mt-3" data-testid="minda-next">
          {next ? (
            <>
              <div className="flex items-center justify-between">
                <p className="font-body text-xs font-bold text-white/85 truncate pr-2">
                  <TrendingUp size={13} className="inline mr-1 text-sunshine" />
                  {en ? 'Next adventure:' : 'Pengembaraan seterusnya:'} {gameName(next.game, en)}
                </p>
                <p className="font-heading text-xs font-bold text-sunshine shrink-0">{Math.min(stars, next.need)}/{next.need} ⭐</p>
              </div>
              <div className="h-3 rounded-full bg-white/15 mt-2 overflow-hidden">
                <motion.div className="h-full rounded-full" style={{ background: 'linear-gradient(90deg, #FFD166, #FF8A65)' }}
                  initial={{ width: 0 }} animate={{ width: `${Math.min(100, (stars / next.need) * 100)}%` }} transition={{ duration: 0.8 }} />
              </div>
            </>
          ) : (
            <p className="font-body text-xs font-bold text-white/85">
              {entitlement?.plan === 'PRO' ? (en ? 'All adventures are open. Amazing!' : 'Semua pengembaraan terbuka. Hebat!') : (en ? 'New worlds await with PintarPlay Pro!' : 'Dunia baharu menanti dengan PintarPlay Pro!')}
            </p>
          )}
        </div>
        <button data-testid="minda-close-btn" onClick={onClose}
          className="w-full mt-4 min-h-[52px] rounded-2xl bg-sunshine font-heading font-bold text-navy"
          style={{ boxShadow: 'inset 0 -5px 0 rgba(0,0,0,0.12)' }}>OK!</button>
      </motion.div>
    </motion.div>
  );
}

export default function ChildHome() {
  const nav = useNavigate();
  const { entitlement } = useEntitlement();
  const [st, setSt] = useState(getState());
  const en = st.profile.language === 'en';
  const stars = st.progress.stars;
  const [showMinda, setShowMinda] = useState(false);
  const [daily, setDaily] = useState(null);
  const [slide, setSlide] = useState(0);
  const railRef = useRef(null);

  const cur = useMemo(() => currentJourney(entitlement, stars, st.gameStats || {}), [entitlement, stars, st.gameStats]);

  const curGame = cur ? getGame(cur.gameId) : null;

  useEffect(() => {
    audio.init(); audio.music('theme_main');
    if (isDailyRewardAvailable()) {
      const sticker = DAILY_STICKERS[dailyRewardCount() % DAILY_STICKERS.length];
      const timer = setTimeout(() => setDaily(sticker), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const claimDaily = () => {
    audio.playSfx('reward');
    audio.playVoice('sticker');
    setSt(claimDailyReward(daily.id));
    setDaily(null);
  };

  useEffect(() => {
    if (cur && railRef.current) {
      const wi = REGIONS.findIndex((r) => r.id === cur.regionId);
      if (wi > 0) railRef.current.scrollTo({ left: wi * railRef.current.clientWidth });
    }
  }, []); // eslint-disable-line

  const onScroll = (e) => setSlide(Math.round(e.target.scrollLeft / e.target.clientWidth));

  const goSlide = (i) => {
    audio.playSfx('tap');
    railRef.current?.scrollTo({ left: i * railRef.current.clientWidth, behavior: 'smooth' });
  };

  const toggleLang = () => {
    setSt(setState({ profile: { ...st.profile, language: en ? 'ms' : 'en' } }));
    audio.playSfx('tap');
  };

  const slides = REGIONS;

  return (
    <div className="fixed inset-0 overflow-hidden flex flex-col" style={{ background: 'linear-gradient(180deg, #1D3557 0%, #2A4A76 55%, #1D3557 100%)' }} data-testid="world-select">
      {/* ambient */}
      {[8, 26, 50, 72, 90].map((x, i) => (
        <motion.div key={i} className="absolute w-2 h-2 rounded-full bg-white/50 pointer-events-none"
          style={{ left: `${x}%`, top: `${10 + (i * 21) % 55}%` }}
          animate={{ y: [0, -18, 0], opacity: [0.1, 0.7, 0.1] }} transition={{ repeat: Infinity, duration: 5 + i, delay: i * 0.6 }} />
      ))}

      {/* ===== HUD ===== */}
      <div className="relative z-20 flex items-center gap-2 px-3 pt-3 shrink-0">
        <div className="flex items-center gap-2 bg-white/90 backdrop-blur rounded-full pl-1.5 pr-3.5 py-1.5 shadow-lg min-w-0" data-testid="profile-chip">
          <img src="/assets/characters/piko_happy.webp" alt="" className="w-9 h-9 rounded-full object-cover" />
          <div className="min-w-0">
            <p className="font-heading text-[13px] font-bold text-navy leading-tight truncate">{st.profile.nickname || 'Adik'}</p>
            {cur && <p className="font-body text-[10px] font-bold text-navy/55 leading-tight whitespace-nowrap" data-testid="journey-progress">
              {en ? `World ${cur.worldIndex} • Game ${cur.gameIndex}/${cur.worldTotal}` : `Dunia ${cur.worldIndex} • Game ${cur.gameIndex}/${cur.worldTotal}`}
            </p>}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button data-testid="minda-open-btn" onClick={() => { audio.playSfx('tap'); setShowMinda(true); }}
            className="flex items-center gap-1.5 bg-white/90 backdrop-blur rounded-full px-3 py-2 shadow-lg min-h-[44px]">
            <Star className="text-sunshine fill-sunshine" size={20} />
            <span className="font-heading font-bold text-navy" data-testid="star-count">{stars}</span>
          </button>
          <button data-testid="lang-toggle-btn" onClick={toggleLang}
            className="flex items-center gap-1 bg-white/90 backdrop-blur rounded-full px-3 py-2 shadow-lg min-h-[44px]">
            <Languages size={16} strokeWidth={2.6} className="text-navy/70" />
            <span className="font-heading font-bold text-xs text-navy/80">{en ? 'EN' : 'MS'}</span>
          </button>
          <button data-testid="parent-corner-btn" onClick={() => nav('/parent-gate')} aria-label={t('parentArea')}
            className="flex items-center gap-1.5 bg-white/90 backdrop-blur rounded-full pl-2.5 pr-3 py-2 shadow-lg min-h-[44px]">
            <ShieldCheck size={17} strokeWidth={2.6} className="text-navy/70" />
            <span className="font-heading font-bold text-xs text-navy/80">{t('parentPanel')}</span>
          </button>
        </div>
      </div>

      <p className="relative z-10 font-heading text-white/80 text-sm font-bold px-6 mt-4 shrink-0">{en ? 'Choose a world' : 'Pilih dunia'}</p>

      {/* ===== WORLD CAROUSEL ===== */}
      <div ref={railRef} onScroll={onScroll}
        className="relative z-10 flex-1 flex overflow-x-auto overflow-y-hidden snap-x snap-mandatory mt-2"
        style={{ scrollbarWidth: 'none' }} data-testid="world-carousel">
        {slides.map((r, wi) => {
          const world = WORLDS[r.id];
          if (r.games.length === 0) {
            // Empty world: "coming soon" — never purchasable, never tappable into a world (Phase 1b)
            return (
              <div key={r.id} className="w-full shrink-0 snap-center px-6 py-2 flex" data-testid={`world-card-coming-${r.id}`}>
                <div className="relative w-full rounded-[2.5rem] overflow-hidden shadow-2xl"
                  style={{ border: '6px dashed rgba(255,255,255,0.55)', background: 'linear-gradient(160deg, #566B8E, #3A4D6E)' }}>
                  <div className="absolute inset-0 opacity-25" style={{ backgroundImage: `url(${world.bg})`, backgroundSize: 'cover', backgroundPosition: 'center', filter: 'grayscale(0.7)' }} />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Gem size={44} className="text-white/70" />
                  </div>
                  <div className="absolute inset-x-0 bottom-0 p-6" style={{ background: 'linear-gradient(0deg, rgba(29,53,87,0.85), transparent)' }}>
                    <h2 className="font-heading text-3xl font-bold text-white/85 drop-shadow-md">{t(world.nameKey)}</h2>
                    <p className="font-body text-sm font-bold text-white/70 mt-1">{en ? 'Coming soon!' : 'Akan datang!'}</p>
                    <p className="font-body text-xs text-white/55 mt-1.5">{world.objective[en ? 'en' : 'ms']}</p>
                  </div>
                </div>
              </div>
            );
          }
          const done = r.games.filter((gid) => (st.gameStats || {})[gid]?.completed).length;
          const isCurWorld = cur?.regionId === r.id;
          const proLocked = !world.free && entitlement?.plan !== 'PRO';
          return (
            <div key={r.id} className="w-full shrink-0 snap-center px-6 py-2 flex" data-testid={`world-card-${r.id}`}>
              <motion.button whileTap={{ scale: 0.97 }}
                onClick={() => { audio.playSfx('pickup'); nav(proLocked ? '/next-adventures' : `/world/${r.id}`); }}
                className="relative w-full rounded-[2.5rem] overflow-hidden shadow-2xl text-left"
                style={{ border: '6px solid rgba(255,255,255,0.92)' }}>
                <motion.div className="absolute inset-[-4%]" animate={{ scale: [1, 1.05, 1] }} transition={{ repeat: Infinity, duration: 16, ease: 'easeInOut' }}
                  style={{ backgroundImage: `url(${world.bg})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                <div className="absolute inset-x-0 top-0 p-5 flex justify-between items-start" style={{ background: 'linear-gradient(180deg, rgba(29,53,87,0.5), transparent)' }}>
                  <span className="font-body text-[11px] font-bold text-white/85 bg-white/20 backdrop-blur rounded-full px-3 py-1 uppercase tracking-wider">{en ? `World ${wi + 1}` : `Dunia ${wi + 1}`}</span>
                  {proLocked ? (
                    <span className="flex items-center gap-1 font-heading text-[11px] font-bold text-sunshine bg-navy/85 rounded-full px-3 py-1 shadow" data-testid={`world-pro-badge-${r.id}`}>
                      <Lock size={11} strokeWidth={3} />PRO
                    </span>
                  ) : isCurWorld && (
                    <motion.span animate={{ scale: [1, 1.08, 1] }} transition={{ repeat: Infinity, duration: 1.8 }}
                      className="font-heading text-[11px] font-bold text-navy bg-sunshine rounded-full px-3 py-1 shadow" data-testid="current-world-badge">
                      {en ? "Piko is here!" : 'Piko di sini!'}
                    </motion.span>
                  )}
                </div>
                <div className="absolute inset-x-0 bottom-0 p-6" style={{ background: 'linear-gradient(0deg, rgba(29,53,87,0.8), transparent)' }}>
                  <h2 className="font-heading text-3xl font-bold text-white drop-shadow-md">{t(world.nameKey)}</h2>
                  <p className="font-body text-xs font-semibold text-white/75 mt-1" data-testid={`world-objective-${r.id}`}>{world.objective[en ? 'en' : 'ms']}</p>
                  {/* The objective is a claim; this is the proof the parent can
                      check at home. Saying what they will SEE is what makes a
                      world feel worth finishing. */}
                  {world.witness && (
                    <p className="font-body text-[11px] font-semibold text-sunshine/95 mt-1 leading-snug" data-testid={`world-witness-${r.id}`}>
                      ✅ {world.witness[en ? 'en' : 'ms']}
                    </p>
                  )}
                  <div className="flex items-center gap-3 mt-3">
                    <div className="flex-1 h-3.5 rounded-full bg-white/25 overflow-hidden">
                      <div className="h-full rounded-full bg-sunshine transition-all" style={{ width: `${(done / r.games.length) * 100}%` }} />
                    </div>
                    <span className="font-heading text-sm font-bold text-white shrink-0" data-testid={`world-progress-${r.id}`}>{done}/{r.games.length}</span>
                  </div>
                  <div className="flex items-center gap-1.5 mt-2.5">
                    {done > 0 && <Check size={16} strokeWidth={3.5} className="text-leaf" />}
                    <span className="font-body text-xs font-semibold text-white/80">
                      {done === r.games.length ? (en ? 'World complete!' : 'Dunia selesai!') : en ? `${done} game completed` : `${done} game selesai`}
                    </span>
                  </div>
                </div>
              </motion.button>
            </div>
          );
        })}
      </div>

      {/* world back/forward arrows */}
      {slide > 0 && (
        <motion.button initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} whileTap={{ scale: 0.88 }}
          data-testid="world-prev-btn" aria-label="Previous world" onClick={() => goSlide(slide - 1)}
          className="absolute left-2 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/95 shadow-xl flex items-center justify-center"
          style={{ boxShadow: 'inset 0 -4px 0 rgba(0,0,0,0.1), 0 8px 20px rgba(0,0,0,0.3)' }}>
          <ChevronLeft size={26} strokeWidth={3} className="text-navy" />
        </motion.button>
      )}
      {slide < slides.length - 1 && (
        <motion.button initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} whileTap={{ scale: 0.88 }}
          data-testid="world-next-btn" aria-label="Next world" onClick={() => goSlide(slide + 1)}
          className="absolute right-2 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-white/95 shadow-xl flex items-center justify-center"
          style={{ boxShadow: 'inset 0 -4px 0 rgba(0,0,0,0.1), 0 8px 20px rgba(0,0,0,0.3)' }}>
          <ChevronRight size={26} strokeWidth={3} className="text-navy" />
        </motion.button>
      )}

      {/* dots */}
      <div className="relative z-10 flex justify-center gap-2 mt-3 shrink-0" data-testid="carousel-dots">
        {slides.map((s, i) => (
          <button key={s.id} data-testid={`carousel-dot-${i}`} aria-label={`World ${i + 1}`} onClick={() => goSlide(i)}
            className={`h-2.5 rounded-full transition-all min-w-0 ${slide === i ? 'w-7 bg-sunshine' : 'w-2.5 bg-white/35'}`} />
        ))}
      </div>

      {/* ===== CONTINUE + NAV ===== */}
      <div className="relative z-10 px-5 pt-4 pb-5 shrink-0">
        {curGame && (
          <motion.button data-testid="continue-playing-btn" whileTap={{ scale: 0.97 }}
            onClick={() => { audio.playSfx('pickup'); audio.playVoice('lets_play'); nav(`/game/${curGame.id}`); }}
            className="w-full min-h-[68px] rounded-[1.8rem] bg-sunshine flex items-center justify-center gap-3 shadow-2xl"
            style={{ boxShadow: 'inset 0 -7px 0 rgba(0,0,0,0.14), 0 12px 30px rgba(0,0,0,0.35)' }}>
            <span className="w-11 h-11 rounded-full bg-navy flex items-center justify-center"><Play size={20} className="text-sunshine fill-sunshine ml-0.5" /></span>
            <span className="text-left">
              <span className="block font-heading text-lg font-bold text-navy leading-tight">{en ? 'Continue Playing' : 'Teruskan Bermain'}</span>
              <span className="block font-body text-xs font-bold text-navy/60">{gameName(curGame, en)}</span>
            </span>
          </motion.button>
        )}
        <div className="flex gap-3 mt-3">
          <button data-testid="garden-btn" onClick={() => nav('/garden')}
            className="flex-1 flex items-center justify-center gap-2 bg-white/90 backdrop-blur rounded-2xl py-3 shadow min-h-[54px]">
            <Flower2 size={20} strokeWidth={2.5} className="text-leaf" />
            <span className="font-heading font-bold text-navy text-sm">{en ? 'Garden' : 'Taman'}</span>
          </button>
          <button data-testid="stickers-btn" onClick={() => nav('/stickers')}
            className="flex-1 flex items-center justify-center gap-2 bg-white/90 backdrop-blur rounded-2xl py-3 shadow min-h-[54px]">
            <BookOpen size={20} strokeWidth={2.5} className="text-coral" />
            <span className="font-heading font-bold text-navy text-sm">{en ? 'Stickers' : 'Pelekat'}</span>
          </button>
        </div>
      </div>

      <AnimatePresence>{showMinda && <MindaModal st={st} stars={stars} entitlement={entitlement} en={en} onClose={() => setShowMinda(false)} />}</AnimatePresence>

      <AnimatePresence>
        {daily && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-navy/60 backdrop-blur-md flex items-center justify-center px-6" data-testid="daily-reward-modal">
            <motion.div initial={{ scale: 0.85, y: 24 }} animate={{ scale: 1, y: 0 }} transition={{ type: 'spring', damping: 16 }}
              className="bg-white/95 rounded-[2.5rem] p-7 max-w-sm w-full text-center shadow-2xl relative overflow-hidden">
              {[10, 30, 55, 75, 90].map((x, i) => (
                <motion.div key={i} className="absolute w-2.5 h-2.5 rounded-full bg-sunshine pointer-events-none"
                  style={{ left: `${x}%`, top: '8%' }}
                  animate={{ y: [0, 260], opacity: [1, 0], rotate: [0, 180] }}
                  transition={{ repeat: Infinity, duration: 2.4 + i * 0.4, delay: i * 0.3 }} />
              ))}
              <div className="relative">
                <Piko pose="celebrate" size={110} float={false} />
                <h3 className="font-heading text-2xl font-bold text-navy mt-2">{en ? 'Daily Gift!' : 'Hadiah Harian!'}</h3>
                <p className="font-body text-sm font-semibold text-navy/60 mt-1">{en ? 'Piko missed you! Here is a sticker for you.' : 'Piko rindu kamu! Ini pelekat untukmu.'}</p>
                <motion.div initial={{ rotate: -12, scale: 0 }} animate={{ rotate: 0, scale: 1 }} transition={{ type: 'spring', delay: 0.3 }}
                  className="w-28 h-28 mx-auto mt-4 rounded-[2rem] bg-cream flex items-center justify-center text-6xl shadow-lg"
                  style={{ border: '5px solid #FFD166' }} data-testid="daily-sticker-emoji">{daily.emoji}</motion.div>
                <p className="font-heading text-lg font-bold text-navy mt-2" data-testid="daily-sticker-name">{daily.name[en ? 1 : 0]}</p>
                <button data-testid="daily-claim-btn" onClick={claimDaily}
                  className="w-full mt-5 min-h-[64px] rounded-2xl bg-sunshine font-heading font-bold text-lg text-navy"
                  style={{ boxShadow: 'inset 0 -6px 0 rgba(0,0,0,0.12)' }}>{en ? 'Yay! Thanks Piko!' : 'Yay! Terima kasih Piko!'}</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}
